import React, { useState } from "react";
import {
  Lock,
  User,
  KeyRound,
  HardDrive,
  Loader2,
  LifeBuoy,
  ArrowLeft,
} from "lucide-react";

const field =
  "w-full bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors placeholder-gray-500";

/**
 * Sign-in dialog. Credentials are verified by the Convex backend, which mints
 * a session token; nothing secret is stored on this device. `onLocalMode`
 * lets someone without a cloud account keep using the app with local storage.
 * `onRecover` ("Forgot passkey?") sets a new passkey with a one-time recovery
 * code and signs in.
 */
const LoginModal = ({ onLogin, onLocalMode, onRecover, error, busy }) => {
  const [username, setUsername] = useState("");
  const [passkey, setPasskey] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (username.trim() && passkey.trim()) {
      onLogin(username.trim(), passkey.trim());
    }
  };

  const handleRecover = (e) => {
    e.preventDefault();
    if (busy) return;
    setLocalError(null);
    if (next.length < 6) {
      setLocalError("The new passkey must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setLocalError("The two new passkeys do not match.");
      return;
    }
    onRecover({ userName: username.trim(), code: code.trim(), next });
  };

  const shownError = localError || error;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-3xl max-w-md w-full border-2 border-cyan-500/30 shadow-2xl animate-fade-in">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Lock className="w-8 h-8 text-cyan-400" />
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-cyan-400">
              {recovering ? "Reset your passkey" : "Welcome to Xunzhine"}
            </h2>
          </div>
          <p className="text-center text-cyan-300 text-sm">
            {recovering
              ? "Use one of your recovery codes to set a new passkey"
              : "Enter your credentials to continue"}
          </p>
        </div>

        {recovering ? (
          <form onSubmit={handleRecover} className="p-6 space-y-5">
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                className={field}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <LifeBuoy className="w-4 h-4" />
                Recovery code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                autoComplete="one-time-code"
                className={`${field} font-mono tracking-wider`}
              />
            </div>
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                New passkey
              </label>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                className={field}
              />
            </div>
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Repeat new passkey
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                className={field}
              />
            </div>

            {shownError ? (
              <div
                role="alert"
                className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-red-300 text-sm text-center"
              >
                {shownError}
              </div>
            ) : (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3">
                <p className="text-cyan-300 text-xs text-center">
                  Recovery codes were shown when you changed your passkey in
                  Settings. Each works once; every other device is signed out.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={
                busy || !username.trim() || !code.trim() || !next || !confirm
              }
              className="w-full bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-black font-bold py-3 px-6 rounded-2xl transition-all flex items-center justify-center gap-2"
            >
              {busy ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <KeyRound className="w-5 h-5" />
              )}
              {busy ? "Resetting…" : "Set new passkey and sign in"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setRecovering(false);
                setLocalError(null);
              }}
              className="w-full text-cyan-300 hover:text-cyan-200 text-sm font-semibold py-2 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Username */}
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                className={field}
                autoFocus
              />
            </div>

            {/* Passkey */}
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                Passkey
              </label>
              <input
                type="password"
                value={passkey}
                onChange={(e) => setPasskey(e.target.value)}
                placeholder="Enter your passkey"
                autoComplete="current-password"
                className={field}
              />
            </div>

            {error ? (
              <div
                role="alert"
                className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-red-300 text-sm text-center"
              >
                {error}
              </div>
            ) : (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3">
                <p className="text-cyan-300 text-xs text-center">
                  Your passkey is checked by the server and never stored on this
                  device — only a sign-in session is kept.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={busy || !username.trim() || !passkey.trim()}
              className="w-full bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-black font-bold py-3 px-6 rounded-2xl transition-all flex items-center justify-center gap-2"
            >
              {busy ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Lock className="w-5 h-5" />
              )}
              {busy ? "Signing in…" : "Continue (only with pro plan)"}
            </button>

            {onRecover && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setRecovering(true);
                  setLocalError(null);
                }}
                className="w-full text-cyan-300/80 hover:text-cyan-200 text-sm py-1 flex items-center justify-center gap-2"
              >
                <LifeBuoy className="w-4 h-4" />
                Forgot passkey?
              </button>
            )}

            {onLocalMode && (
              <button
                type="button"
                disabled={busy || !username.trim()}
                onClick={() => onLocalMode(username.trim())}
                className="w-full bg-gray-700/60 hover:bg-gray-700 text-cyan-200 font-semibold py-3 px-6 rounded-2xl border-2 border-cyan-500/40 hover:border-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-700/60 disabled:hover:border-cyan-500/40 transition-colors flex items-center justify-center gap-2"
                title="No cloud account — keep everything on this device only"
              >
                <HardDrive className="w-5 h-5" />
                Use local storage only (no cloud sync)
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
